/**
 * /example — "Not a black box"
 *
 * A public page showing one complete agent run and one honest failure, with no
 * signup. Built in response to two strangers on the launch post who pointed out
 * that the product's observability is real but invisible to anyone deciding
 * whether to trust it.
 *
 * SUCCESS CRITERION (do not move the goalposts): this page passes only if it
 * moves someone into "can it do X for my client?" — a specific workflow ask.
 * Generic "looks transparent" praise means the page worked and the offer still
 * doesn't. See docs/marketing/MARKETING-DASHBOARD.md.
 */
import { Link } from 'react-router-dom';
import { BrandMark } from '../components/BrandMark';
import {
  COMPLETED_RUN,
  FAILED_RUN,
  type ExampleRun,
  type ExampleStep,
} from '../data/exampleRun';
import {
  formatCents,
  formatDuration,
  formatTokens,
} from '../hooks/useExecution';

export default function ExampleRun() {
  return (
    <div className="min-h-screen bg-[var(--paper)] text-[var(--ink)]">
      <Header />

      <main className="mx-auto max-w-3xl px-5 py-12">
        <span className="lp-eyebrow">See a real run</span>
        <h1 className="lp-display mt-3 text-3xl sm:text-4xl">
          Not a black box.
        </h1>
        <p className="mt-4 max-w-2xl text-[var(--ink-soft)]">
          Every run shows its input, what each agent actually did, the tokens and
          the real cost per step, and the final output — including when it fails.
          This is the same view you get on your own runs. No account needed to
          read it.
        </p>

        <RunCard run={COMPLETED_RUN} />

        <div className="mt-14">
          <h2 className="lp-display text-2xl">And when it goes wrong</h2>
          <p className="mt-3 max-w-2xl text-[var(--ink-soft)]">
            A demo that only shows the happy path is the black box with better
            lighting. Here is a real failure state: the tool it needed was down,
            so the run stopped and said so — it did not invent an answer. You
            still see exactly where and why, and what it cost up to that point.
          </p>
        </div>

        <RunCard run={FAILED_RUN} />

        <CtaBlock />
      </main>

      <Footer />
    </div>
  );
}

function Header() {
  return (
    <header className="border-b border-[var(--line,#e5e7eb)]">
      <div className="mx-auto flex max-w-5xl items-center justify-between px-5 py-4">
        <Link to="/" className="flex items-center gap-2 font-bold">
          <BrandMark className="h-7 w-7" />
          AgentMesh
        </Link>
        <Link to="/register" className="lp-btn lp-btn-primary text-sm">
          Start free
        </Link>
      </div>
    </header>
  );
}

function RunCard({ run }: { run: ExampleRun }) {
  const failed = run.status === 'FAILED';
  return (
    <section className="lp-card mt-8 overflow-hidden">
      {/* Run header */}
      <div className="border-b border-[var(--line,#e5e7eb)] p-5">
        <div className="flex flex-wrap items-center gap-3">
          <StatusPill status={run.status} />
          <span className="font-semibold">{run.title}</span>
        </div>
        <p className="lp-mono mt-2 text-xs text-[var(--ink-soft)]">
          {run.id} · {run.workflowType} workflow
        </p>
      </div>

      {/* Summary metrics — mirrors the real run-detail cards */}
      <div className="grid grid-cols-2 gap-px bg-[var(--line,#e5e7eb)] sm:grid-cols-3">
        <Metric label="Duration" value={formatDuration(run.durationMs)} />
        <Metric label="Tokens" value={formatTokens(run.totalTokens)} />
        <Metric
          label="Cost"
          value={formatCents(run.totalCostCents)}
          sub="Estimated"
        />
      </div>

      {/* Input */}
      <Block label="Input">
        <pre className="lp-mono whitespace-pre-wrap text-sm text-[var(--ink)]">
          {run.input}
        </pre>
      </Block>

      {/* Steps */}
      <div className="border-t border-[var(--line,#e5e7eb)] p-5">
        <p className="lp-eyebrow mb-3">Steps</p>
        <ol className="space-y-4">
          {run.steps.map((s) => (
            <StepRow key={s.n} step={s} />
          ))}
        </ol>
      </div>

      {/* Final output / failure */}
      {failed ? (
        <Block label="Result" tone="fail">
          <p className="text-sm">
            Run stopped at step {run.steps.length}. No partial answer was
            fabricated — a failed run is reported as failed.
          </p>
        </Block>
      ) : (
        <Block label="Result" tone="ok">
          <p className="text-sm">{run.finalOutput}</p>
        </Block>
      )}
    </section>
  );
}

function StepRow({ step }: { step: ExampleStep }) {
  const failed = step.status === 'FAILED';
  return (
    <li className="rounded-lg border border-[var(--line,#e5e7eb)] p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[var(--ink)] text-xs font-semibold text-white">
            {step.n}
          </span>
          <span className="font-medium">{step.agent}</span>
          <StatusPill status={step.status} small />
        </div>
        <div className="lp-mono flex items-center gap-3 text-xs text-[var(--ink-soft)]">
          <span>{formatTokens(step.tokens)} tok</span>
          <span>{formatCents(step.costCents)}</span>
          <span>{formatDuration(step.durationMs)}</span>
        </div>
      </div>

      <p className="mt-3 text-sm text-[var(--ink-soft)]">{step.action}</p>

      {step.output && (
        <pre className="lp-mono mt-3 overflow-x-auto rounded-md bg-[color-mix(in_srgb,var(--ink)_5%,transparent)] p-3 text-xs">
          {step.output}
        </pre>
      )}
      {failed && step.error && (
        <pre className="lp-mono mt-3 overflow-x-auto rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-700">
          {step.error}
        </pre>
      )}
    </li>
  );
}

function Metric({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="bg-[var(--paper)] p-4">
      <p className="lp-eyebrow">{label}</p>
      <p className="mt-1 text-xl font-bold">{value}</p>
      {sub && <p className="text-xs text-[var(--ink-soft)]">{sub}</p>}
    </div>
  );
}

function Block({
  label,
  children,
  tone,
}: {
  label: string;
  children: React.ReactNode;
  tone?: 'ok' | 'fail';
}) {
  const ring =
    tone === 'ok'
      ? 'border-l-4 border-l-[var(--signal,#057f5d)]'
      : tone === 'fail'
        ? 'border-l-4 border-l-red-400'
        : '';
  return (
    <div className={`border-t border-[var(--line,#e5e7eb)] p-5 ${ring}`}>
      <p className="lp-eyebrow mb-2">{label}</p>
      {children}
    </div>
  );
}

function StatusPill({
  status,
  small,
}: {
  status: 'COMPLETED' | 'FAILED';
  small?: boolean;
}) {
  const ok = status === 'COMPLETED';
  return (
    <span
      className={`inline-flex items-center rounded-full font-medium ${
        small ? 'px-2 py-0.5 text-xs' : 'px-3 py-1 text-sm'
      } ${ok ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}
    >
      {status}
    </span>
  );
}

function CtaBlock() {
  return (
    <div className="lp-card mt-14 p-8 text-center">
      <h2 className="lp-display text-2xl">
        Want to see one of your own?
      </h2>
      <p className="mx-auto mt-3 max-w-md text-[var(--ink-soft)]">
        The free tier is real — no card. Build an agent, run it, and you get this
        exact view of what it did and what it cost.
      </p>
      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <Link to="/register" className="lp-btn lp-btn-primary">
          Start free — no card
        </Link>
        <Link to="/help/getting-started" className="lp-btn lp-btn-ghost">
          Read the guide
        </Link>
      </div>
      <p className="lp-mono mt-6 text-xs text-[var(--ink-soft)]">
        Built by one person. Costs above are real, computed the same way the
        product computes them.
      </p>
    </div>
  );
}

function Footer() {
  return (
    <footer className="border-t border-[var(--line,#e5e7eb)]">
      <div className="mx-auto flex max-w-5xl flex-wrap gap-4 px-5 py-6 text-sm text-[var(--ink-soft)]">
        <Link to="/">Home</Link>
        <Link to="/help">Help</Link>
        <Link to="/pricing">Pricing</Link>
        <Link to="/docs">Docs</Link>
        <span>© 2026 AgentMesh</span>
      </div>
    </footer>
  );
}
